import { matchPath, useLocation } from "react-router-dom";
import useSEO from "../../hooks/useSEO";
import publicSeoRoutes from "../../seo/publicSeoRoutes.json";

const SITE_URL = "https://skilll.dev";
const DEFAULT_OG_IMAGE = `${SITE_URL}/logo.png`;
const DEFAULT_KEYWORDS =
  "skilll.dev, developer roadmap, coding roadmap, career clarity, ai roadmap assistant, daily learning plan";

function withSiteTitle(title) {
  return `${title} | skilll.dev`;
}

function getSEOForPath(pathname) {
  for (const route of publicSeoRoutes) {
    const matched = matchPath({ path: route.path, end: true }, pathname);
    if (matched) {
      return route;
    }
  }

  const privatePatterns = [
    "/profile",
    "/profile/:uid",
    "/create-roadmap",
    "/my-roadmap",
    "/dashboard",
    "/community/feeds",
    "/community/feeds/create",
    "/edit-post/:postId",
    "/roadmap/calendar",
    "/editor",
    "/tasks-board/day",
    "/spring-board",
    "/product-generator",
    "/focus-without-phone",
    "/ai-mentors",
    "/ai-mentor-chat",
    "/ai-mentor-chat/:mentorId",
    "/roadmap/day",
    "/support",
    "/google-calendar/success",
    "/api/google/callback",
  ];

  if (
    privatePatterns.some((path) =>
      matchPath({ path, end: true }, pathname)
    )
  ) {
    return {
      title: withSiteTitle("Developer Roadmap Workspace"),
      description:
        "Private product workspace inside skilll.dev for roadmap execution, progress, support, and consistency systems.",
      keywords: DEFAULT_KEYWORDS,
      robots: "noindex,nofollow",
    };
  }

  if (pathname === "/") {
    return {
      title: withSiteTitle("Developer Roadmaps, Product Building, and Interview Prep"),
      description:
        "skilll.dev helps developers learn, build products, and prepare for interviews with roadmap execution, AI guidance, reminders, and daily consistency systems.",
      keywords:
        "skilll.dev, developer roadmap platform, product development roadmap, interview prep roadmap, ai roadmap assistant",
      robots: "index,follow",
    };
  }

  return {
    title: withSiteTitle("Developer Career Roadmap Platform"),
    description:
      "skilll.dev helps developers get clarity, follow one roadmap daily, and stay consistent with execution systems.",
    keywords: DEFAULT_KEYWORDS,
    robots: "index,follow",
  };
}

export default function RouteSEO() {
  const location = useLocation();
  const seo = getSEOForPath(location.pathname);

  const pathname = location.pathname;
  const canonical = `${SITE_URL}${pathname}`;
  let title = seo.title;
  let description = seo.description;

  if (matchPath({ path: "/code-practice/:language", end: true }, pathname)) {
    const languageSlug = pathname.split("/").filter(Boolean).pop() || "code";
    const language =
      languageSlug.charAt(0).toUpperCase() + languageSlug.slice(1);
    title = withSiteTitle(`${language} Code Practice`);
    description = `Practice ${language} in a focused coding workspace with an editor, output panel, and distraction-free layout.`;
  }

  useSEO({
    title,
    description,
    keywords: seo.keywords || DEFAULT_KEYWORDS,
    canonical,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: "website",
    robots: seo.robots,
  });

  return null;
}
